import { CaretUpDown } from "@phosphor-icons/react";
import clsx from "clsx";
import { useCombobox } from "downshift";
import ISO6391 from "iso-639-1";
import { useAtom } from "jotai";
import { useEffect, useId, useState } from "react";

import { queueAtom } from "~/atoms/queue";

const languages = ISO6391.getLanguages(ISO6391.getAllCodes());

export const LanguagePicker: React.FC<{
  defaultValue?: string | null;
}> = ({ defaultValue }) => {
  const id = useId();
  const [, setQueue] = useAtom(queueAtom);
  const [items, setItems] = useState(languages);

  const {
    isOpen,
    selectedItem,
    highlightedIndex,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    getToggleButtonProps,
  } = useCombobox({
    items,
    initialSelectedItem: defaultValue
      ? languages.find((lang) => lang.code === defaultValue)
      : null,
    itemToString: (item) => item?.name ?? "",
    onInputValueChange: ({ inputValue }) => {
      if (!inputValue) {
        setItems(languages);
        return;
      }

      const query = inputValue.toLowerCase();
      setItems(
        languages.filter(
          (lang) =>
            lang.code.toLowerCase() === query ||
            lang.name.toLowerCase().includes(query) ||
            lang.nativeName.toLowerCase().includes(query),
        ),
      );
    },
  });

  useEffect(() => {
    if (isOpen) {
      setQueue((prev) => (prev.includes(id) ? prev : [id, ...prev]));
    } else if (!selectedItem) {
      setQueue((prev) => prev.filter((item) => item !== id));
    }
  }, [isOpen, selectedItem, setQueue, id]);

  return (
    <>
      <input type="hidden" name="language" value={selectedItem?.code ?? ""} />
      <div className="form-control">
        <label className="label label-text" {...getLabelProps()}>
          Language
        </label>
        <div className="relative flex flex-col">
          <input
            className="input input-bordered"
            placeholder="Start typing a language..."
            {...getInputProps()}
          />
          <button
            type="button"
            className="btn btn-square btn-sm absolute right-2 top-2"
            {...getToggleButtonProps()}
          >
            <CaretUpDown className="size-6" />
          </button>
          <div {...getMenuProps()}>
            {isOpen && items.length > 0 && (
              <ul className="menu absolute bottom-full z-10 mb-1 block max-h-52 w-full overflow-auto rounded-box border bg-base-100 shadow">
                {items.map((item, index) => (
                  <li key={item.code} {...getItemProps({ item, index })}>
                    <span
                      className={clsx(
                        highlightedIndex === index && "bg-base-300",
                        selectedItem?.code === item.code && "active",
                      )}
                    >
                      {item.name !== item.nativeName
                        ? `${item.name} (${item.nativeName})`
                        : item.name}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
